import React, { useState } from "react";
import { startAuthentication } from "@simplewebauthn/browser";
import { ShieldCheck, Fingerprint, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";
import { api } from "../lib/api";

/**
 * Second-factor challenge shown after password (or Google SSO) succeeds for
 * an account that has MFA enrolled. Supports TOTP (authenticator app) and
 * Passkey — never SMS, by design (less secure, intentionally not offered).
 */
export default function MfaVerifyForm({ mfaToken, methods, onVerified, onBack }) {
  const { toast } = useToast();
  const [method, setMethod] = useState(methods?.totp ? "totp" : "passkey");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const verifyTotp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, method: "totp", code });
      onVerified(data.access_token, data.user);
    } catch (err) {
      toast({ title: "Couldn't verify code", description: err?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const verifyPasskey = async () => {
    setBusy(true);
    try {
      const { data: opts } = await api.post("/auth/mfa/passkey/login-options", { mfa_token: mfaToken });
      const credential = await startAuthentication({ optionsJSON: JSON.parse(opts.options) });
      const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, method: "passkey", credential });
      onVerified(data.access_token, data.user);
    } catch (err) {
      toast({ title: "Passkey sign-in failed", description: err?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card shadow-xl shadow-primary/5 p-6 sm:p-8" data-testid="mfa-verify-card">
      <div className="flex items-center gap-2 text-primary text-[12px] font-semibold uppercase tracking-wider">
        <ShieldCheck className="w-4 h-4" /> Verify it's you
      </div>
      <h1 className="mt-2 text-[22px] font-semibold tracking-tight">Two-factor authentication</h1>
      <p className="text-[13px] text-muted-foreground mt-1">This account has extra protection enabled.</p>

      {methods?.totp && methods?.passkey && (
        <div className="mt-5 grid grid-cols-2 gap-2 p-1 bg-muted rounded-lg">
          <button type="button" data-testid="mfa-method-totp-tab" onClick={() => setMethod("totp")}
            className={`h-8 rounded-md text-[12.5px] font-medium transition-colors ${method === "totp" ? "bg-background shadow-sm" : "text-muted-foreground"}`}>
            Authenticator app
          </button>
          <button type="button" data-testid="mfa-method-passkey-tab" onClick={() => setMethod("passkey")}
            className={`h-8 rounded-md text-[12.5px] font-medium transition-colors ${method === "passkey" ? "bg-background shadow-sm" : "text-muted-foreground"}`}>
            Passkey
          </button>
        </div>
      )}

      {method === "totp" ? (
        <form onSubmit={verifyTotp} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="mfa-code">6-digit code</Label>
            <Input id="mfa-code" data-testid="mfa-totp-code-input" inputMode="numeric" autoComplete="one-time-code"
              maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456" autoFocus required className="tracking-[0.4em] text-center font-mono text-lg" />
            <p className="text-[11.5px] text-muted-foreground">From your authenticator app (Google Authenticator, Authy, 1Password…).</p>
          </div>
          <Button type="submit" data-testid="mfa-verify-totp-button" disabled={busy || code.length !== 6} className="w-full h-10">
            {busy ? "Verifying…" : "Verify & continue"}
          </Button>
        </form>
      ) : (
        <div className="mt-6 space-y-4">
          <div className="rounded-lg border border-dashed border-border p-6 text-center">
            <Fingerprint className="w-8 h-8 mx-auto text-primary mb-2" />
            <p className="text-[13px] text-muted-foreground">Use your device's biometrics, security key, or screen lock.</p>
          </div>
          <Button type="button" data-testid="mfa-verify-passkey-button" onClick={verifyPasskey} disabled={busy} className="w-full h-10 gap-2">
            <Fingerprint className="w-4 h-4" /> {busy ? "Waiting for passkey…" : "Use passkey"}
          </Button>
        </div>
      )}

      <button type="button" data-testid="mfa-back-to-login-button" onClick={onBack}
        className="mt-5 flex items-center gap-1.5 text-[12.5px] text-muted-foreground hover:text-foreground mx-auto">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
      </button>
    </div>
  );
}
